import { SalesStage, SalesStageColor } from 'models/sales_stages'
import { colors } from 'styles/tokens'

/** Terminal stage names that always appear at the end of the pipeline. */
const TERMINAL_STAGE_NAMES = new Set(['closed won', 'closed lost', 'churn'])

/** Gray fill for inactive/placeholder stage chevrons. */
export const INACTIVE_STAGE_COLOR = colors['surface-disabled']

/** Neutral fallback color for stages with no resolved color. */
export const DEFAULT_STAGE_COLOR = colors['ds2026-badge-gray-bg']

/** Check if a stage is a terminal stage (Closed Won, Closed Lost, Churn). */
export function isTerminalStage(stage: SalesStage): boolean {
  return TERMINAL_STAGE_NAMES.has(stage.stage.toLowerCase())
}

/**
 * Sort stages into canonical pipeline order: custom order when enabled,
 * otherwise avg_days_to_close DESC (longest first = earliest pipeline stage).
 */
export function sortStagesPipelineOrder(
  stages: SalesStage[],
  customOrderEnabled: boolean
): SalesStage[] {
  return [...stages].sort((a, b) =>
    customOrderEnabled
      ? a.order - b.order
      : (b.avg_days_to_close ?? 0) - (a.avg_days_to_close ?? 0)
  )
}

// ─── Per-stage color palette (gated, AA-vetted) ──────────────────────────────
//
// Supersedes the phase-bucket coloring: each pipeline stage owns ONE color from
// this fixed palette (text-700 on bg-50, all ≥4.5:1 contrast). Class strings are
// written out in full (never template-built) so Tailwind's JIT can see them.
// Border uses the `!`-prefixed stroke to beat Bootstrap's `.border` !important.

/** A rendered stage color: badge fill + text + solid dot + colored stroke. */
export interface StageTone {
  bg: string
  text: string
  dot: string
  stroke: string
}

/** Palette display order (rough spectrum). Matches the `SalesStageColor` union
 *  and the backend `SalesStageColor` choices. */
export const STAGE_PALETTE_KEYS = [
  'blue',
  'indigo',
  'sky',
  'cyan',
  'teal',
  'emerald',
  'lime',
  'yellow',
  'amber',
  'orange',
  'rose',
  'pink',
  'fuchsia',
  'purple',
  'violet',
] as const satisfies readonly SalesStageColor[]

export const STAGE_PALETTE: Record<SalesStageColor, StageTone> = {
  blue: {
    bg: 'bg-blue-50',
    text: 'text-blue-700',
    dot: 'bg-blue-500',
    stroke: '!border-blue-400',
  },
  indigo: {
    bg: 'bg-indigo-50',
    text: 'text-indigo-700',
    dot: 'bg-indigo-500',
    stroke: '!border-indigo-400',
  },
  sky: {
    bg: 'bg-sky-50',
    text: 'text-sky-700',
    dot: 'bg-sky-500',
    stroke: '!border-sky-400',
  },
  cyan: {
    bg: 'bg-cyan-50',
    text: 'text-cyan-700',
    dot: 'bg-cyan-500',
    stroke: '!border-cyan-400',
  },
  teal: {
    bg: 'bg-teal-50',
    text: 'text-teal-700',
    dot: 'bg-teal-500',
    stroke: '!border-teal-400',
  },
  emerald: {
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    dot: 'bg-emerald-500',
    stroke: '!border-emerald-400',
  },
  lime: {
    bg: 'bg-lime-50',
    text: 'text-lime-700',
    dot: 'bg-lime-500',
    stroke: '!border-lime-400',
  },
  yellow: {
    bg: 'bg-yellow-50',
    text: 'text-yellow-700',
    dot: 'bg-yellow-500',
    stroke: '!border-yellow-400',
  },
  amber: {
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    dot: 'bg-amber-500',
    stroke: '!border-amber-400',
  },
  orange: {
    bg: 'bg-orange-50',
    text: 'text-orange-700',
    dot: 'bg-orange-500',
    stroke: '!border-orange-400',
  },
  rose: {
    bg: 'bg-rose-50',
    text: 'text-rose-700',
    dot: 'bg-rose-500',
    stroke: '!border-rose-400',
  },
  pink: {
    bg: 'bg-pink-50',
    text: 'text-pink-700',
    dot: 'bg-pink-500',
    stroke: '!border-pink-400',
  },
  fuchsia: {
    bg: 'bg-fuchsia-50',
    text: 'text-fuchsia-700',
    dot: 'bg-fuchsia-500',
    stroke: '!border-fuchsia-400',
  },
  purple: {
    bg: 'bg-purple-50',
    text: 'text-purple-700',
    dot: 'bg-purple-500',
    stroke: '!border-purple-400',
  },
  violet: {
    bg: 'bg-violet-50',
    text: 'text-violet-700',
    dot: 'bg-violet-500',
    stroke: '!border-violet-400',
  },
}

/** Human-readable labels for the color picker. */
export const STAGE_COLOR_LABELS: Record<SalesStageColor, string> = {
  blue: 'Blue',
  indigo: 'Indigo',
  sky: 'Sky',
  cyan: 'Cyan',
  teal: 'Teal',
  emerald: 'Emerald',
  lime: 'Lime',
  yellow: 'Yellow',
  amber: 'Amber',
  orange: 'Orange',
  rose: 'Rose',
  pink: 'Pink',
  fuchsia: 'Fuchsia',
  purple: 'Purple',
  violet: 'Violet',
}

/** Locked semantic tones for terminal outcomes (never user-customizable). */
export const TERMINAL_TONES: Record<'won' | 'lost' | 'churn', StageTone> = {
  won: {
    bg: 'bg-green-50',
    text: 'text-green-700',
    dot: 'bg-green-600',
    stroke: '!border-green-400',
  },
  lost: {
    bg: 'bg-red-50',
    text: 'text-red-700',
    dot: 'bg-red-600',
    stroke: '!border-red-400',
  },
  churn: {
    bg: 'bg-gray-100',
    text: 'text-gray-700',
    dot: 'bg-gray-500',
    stroke: '!border-gray-400',
  },
}

/** Icon-agnostic classification: terminals keep semantic identity, every other
 *  stage is just 'pipeline' (one shared icon). Icon mapping lives in consumers
 *  to keep this module free of icon-library coupling. */
export type StageKind = 'pipeline' | 'won' | 'lost' | 'churn'

export function getStageKind(stage: SalesStage): StageKind {
  if (!isTerminalStage(stage)) return 'pipeline'
  const name = stage.stage.toLowerCase()
  return name === 'closed won'
    ? 'won'
    : name === 'closed lost'
      ? 'lost'
      : 'churn'
}

/** Palette key derived purely from a pipeline stage's position, cycling so
 *  adjacent stages always differ even past 15 stages. (Terminals never reach
 *  here — callers handle them first.) */
function derivePipelineColorKey(
  stage: SalesStage,
  allStages: SalesStage[],
  customOrderEnabled: boolean
): SalesStageColor {
  const regulars = sortStagesPipelineOrder(
    allStages,
    customOrderEnabled
  ).filter((s) => !isTerminalStage(s))
  const idx = regulars.findIndex((s) => s.id === stage.id)
  return STAGE_PALETTE_KEYS[(idx < 0 ? 0 : idx) % STAGE_PALETTE_KEYS.length]
}

/**
 * The effective palette color KEY a stage renders as — its stored `color`, or
 * the one derived from pipeline position. Returns null for terminal stages
 * (they use fixed semantic colors, not the palette). The single source of truth
 * for per-stage color resolution; use it to detect which colors are actually in
 * use (stored OR derived), e.g. the picker's duplicate warnings.
 */
export function getStageColorKey(
  stage: SalesStage,
  allStages: SalesStage[],
  customOrderEnabled: boolean
): SalesStageColor | null {
  if (isTerminalStage(stage)) return null
  if (stage.color && stage.color in STAGE_PALETTE) return stage.color
  return derivePipelineColorKey(stage, allStages, customOrderEnabled)
}

/**
 * Resolve the rendered (class-based) color for a stage: terminal stages → locked
 * semantic tone (won/lost/churn); every other stage → its palette tone from
 * `getStageColorKey` (stored color, else derived from position).
 */
export function getStageColor(
  stage: SalesStage,
  allStages: SalesStage[],
  customOrderEnabled: boolean
): StageTone {
  const kind = getStageKind(stage)
  if (kind !== 'pipeline') return TERMINAL_TONES[kind]
  // Pipeline stages always resolve to a non-null palette key.
  const key = getStageColorKey(stage, allStages, customOrderEnabled)!
  return STAGE_PALETTE[key]
}

// ─── HEX stage colors (for hex-only consumers like TextPill) ─────────────────
//
// StagePill renders through <TextPill>, which takes HEX strings for
// background/text rather than Tailwind classes. These use the DS2026 badge
// tokens so the badges match the legend/settings swatches. All values are
// sourced from design tokens (single source of truth).

/** HEX colors for a rendered stage badge. */
export interface PhaseHex {
  /** Soft 50-level fill — used behind dark text on stage pills/badges. */
  bg: string
  /** Dark 700-level text color for those pills/badges. */
  text: string
  /** Vivid 500/600-level fill — for solid shapes (chart bars, progress
   *  segments) where the washed-out `bg` reads as too light. Matches the
   *  strong `.dot` swatch hue used in menus/legends. */
  solid: string
  /** Lighter solid fill — the soft counterpart to `solid`, for hex consumers
   *  that step a fill's intensity. Terminal phases (won/lost/churn) reuse their
   *  `solid` here since terminal stages always render at strong. */
  solidSoft: string
}

/** Terminal-stage → badge HEX colors, drawn from DS2026 badge tokens. */
export const PHASE_HEX: Record<'won' | 'lost' | 'churn', PhaseHex> = {
  won: {
    bg: colors['ds2026-badge-success-bg'],
    text: colors['ds2026-badge-success-text'],
    solid: colors['ds2026-badge-success-icon'],
    // Terminal: always strong, so soft mirrors solid.
    solidSoft: colors['ds2026-badge-success-icon'],
  },
  lost: {
    bg: colors['ds2026-badge-error-bg'],
    text: colors['ds2026-badge-error-text'],
    solid: colors['ds2026-badge-error-icon'],
    // Terminal: always strong, so soft mirrors solid.
    solidSoft: colors['ds2026-badge-error-icon'],
  },
  churn: {
    bg: colors['ds2026-badge-gray-bg'],
    text: colors['ds2026-badge-gray-text'],
    solid: colors['ds2026-badge-gray-icon'],
    // Terminal: always strong, so soft mirrors solid.
    solidSoft: colors['ds2026-badge-gray-icon'],
  },
}

// ─── Per-stage HEX colors (hex twin of STAGE_PALETTE) ────────────────────────
//
// For hex-only consumers (TextPill badges, chart segments). Values are Tailwind
// 50 (bg) / 700 (text) / 500 (solid) — identical to the DS2026 badge tokens for
// the hues that have them. `solidSoft` mirrors `solid` since the per-stage model
// has no soft/strong intensity (each stage owns a distinct hue). If this ships,
// the hues without a DS2026 token (indigo/emerald/lime/yellow/fuchsia) should
// get proper tokens.
export const STAGE_HEX: Record<SalesStageColor, PhaseHex> = {
  blue: {
    bg: '#EFF6FF',
    text: '#1D4ED8',
    solid: '#3B82F6',
    solidSoft: '#3B82F6',
  },
  indigo: {
    bg: '#EEF2FF',
    text: '#4338CA',
    solid: '#6366F1',
    solidSoft: '#6366F1',
  },
  sky: {
    bg: '#F0F9FF',
    text: '#0369A1',
    solid: '#0EA5E9',
    solidSoft: '#0EA5E9',
  },
  cyan: {
    bg: '#ECFEFF',
    text: '#0E7490',
    solid: '#06B6D4',
    solidSoft: '#06B6D4',
  },
  teal: {
    bg: '#F0FDFA',
    text: '#0F766E',
    solid: '#14B8A6',
    solidSoft: '#14B8A6',
  },
  emerald: {
    bg: '#ECFDF5',
    text: '#047857',
    solid: '#10B981',
    solidSoft: '#10B981',
  },
  lime: {
    bg: '#F7FEE7',
    text: '#4D7C0F',
    solid: '#84CC16',
    solidSoft: '#84CC16',
  },
  yellow: {
    bg: '#FEFCE8',
    text: '#A16207',
    solid: '#EAB308',
    solidSoft: '#EAB308',
  },
  amber: {
    bg: '#FFFBEB',
    text: '#B45309',
    solid: '#F59E0B',
    solidSoft: '#F59E0B',
  },
  orange: {
    bg: '#FFF7ED',
    text: '#C2410C',
    solid: '#F97316',
    solidSoft: '#F97316',
  },
  rose: {
    bg: '#FFF1F2',
    text: '#BE123C',
    solid: '#F43F5E',
    solidSoft: '#F43F5E',
  },
  pink: {
    bg: '#FDF2F8',
    text: '#BE185D',
    solid: '#EC4899',
    solidSoft: '#EC4899',
  },
  fuchsia: {
    bg: '#FDF4FF',
    text: '#A21CAF',
    solid: '#D946EF',
    solidSoft: '#D946EF',
  },
  purple: {
    bg: '#FAF5FF',
    text: '#7E22CE',
    solid: '#A855F7',
    solidSoft: '#A855F7',
  },
  violet: {
    bg: '#F5F3FF',
    text: '#6D28D9',
    solid: '#8B5CF6',
    solidSoft: '#8B5CF6',
  },
}

/** HEX twin of getStageColor: terminal→semantic, else the palette hex for the
 *  key from getStageColorKey (stored color, else derived from position). */
export function getStageColorHex(
  stage: SalesStage,
  allStages: SalesStage[],
  customOrderEnabled: boolean
): PhaseHex {
  const kind = getStageKind(stage)
  if (kind !== 'pipeline') return PHASE_HEX[kind]
  // Pipeline stages always resolve to a non-null palette key.
  const key = getStageColorKey(stage, allStages, customOrderEnabled)!
  return STAGE_HEX[key]
}

/** Solid fill HEX for chart segments / bars under the per-stage color model. */
export function getStageColorSolid(
  stage: SalesStage,
  allStages: SalesStage[],
  customOrderEnabled: boolean
): string {
  return getStageColorHex(stage, allStages, customOrderEnabled).solid
}
